// GPS & Load — Trends (2026-09-28). Per player and GPS parameter: up / down / stable over the
// last 8 matches (60+ min, per 90) and the last 8 complete training weeks (vs the squad's weekly
// median), with max 3 conclusions. Everything — series, Theil-Sen fit, classification,
// role, conclusions, headline — comes from GET /api/gps/trends (services/trends/); this page only
// renders it. Bands are calibrated for 8-point windows, so there is deliberately no window
// selector. Colour means direction only, never good or bad (see trendsShared.js).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getGpsTrends, setTrendRoleOverride, clearTrendRoleOverride } from '../../api';
import { useUrlParam } from '../../hooks/useUrlParam';
import TrendsPlayerDrawer from './TrendsPlayerDrawer';
import {
  TRENDS_CSS, METRICS, BLOCK_LABEL, POSITION_ORDER, NOT_APPLICABLE_LABEL,
  StateChip, Sparkline, blockSlots, cellTitle, fmtPct, fmtDate, stateVisual,
} from './trendsShared';

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

function useIsNarrow(maxWidth = 899) {
  const query = `(max-width: ${maxWidth}px)`;
  const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' && window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const on = () => setNarrow(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, [query]);
  return narrow;
}

// Summary strip wording, in display order. A player counts once, under his headline (most notable
// state across both blocks — computed server-side).
const SUMMARY_ORDER = [
  { key: 'stable', label: 'stable' },
  { key: 'down', label: 'falling' },
  { key: 'up', label: 'rising' },
  { key: 'irregular', label: 'irregular' },
  { key: 'rehab', label: 'rehab' },
  { key: 'insufficient', label: 'insufficient data' },
];
const summaryKey = h => (h.level === 'clear' || h.level === 'weak' ? h.direction : h.level);
const hasTrend = p => p.headline.level === 'clear' || p.headline.level === 'weak';

const sortPlayers = list => [...list].sort((a, b) => {
  const pa = POSITION_ORDER.indexOf(a.position), pb = POSITION_ORDER.indexOf(b.position);
  return (pa < 0 ? 99 : pa) - (pb < 0 ? 99 : pb) || a.name.localeCompare(b.name);
});

// Why a block has no trend at all, as one short line.
function blockStatusText(player, block) {
  if (block === 'match') return `— (n = ${player.match.n})`;
  const t = player.training;
  if (t.weeks.every(w => w.excluded === 'no_training')) return t.lastGpsDate ? `— no GPS data since ${fmtDate(t.lastGpsDate)}` : '— no GPS data';
  if (t.state === 'rehab') return `— rehab in ${t.rehabWeeks} of ${t.weeks.length} weeks`;
  return `— too few valid weeks (n = ${t.nValid})`;
}

// ── Pieces ─────────────────────────────────────────────────────────────────────────────────────
const th = { textAlign: 'left', padding: '8px 8px', fontSize: 10.5, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.4, whiteSpace: 'nowrap', borderBottom: '1px solid var(--border)' };
const td = { padding: '7px 8px', borderBottom: '1px solid var(--border)', verticalAlign: 'top' };

function BlockSwitch({ value, onChange, label }) {
  return (
    <div role="radiogroup" aria-label={`Block for ${label}`} style={{ display: 'inline-flex', border: '1px solid var(--border-strong)', borderRadius: 8, overflow: 'hidden' }}>
      {['match', 'training'].map(b => (
        <button key={b} role="radio" aria-checked={value === b} onClick={() => onChange(b)}
          style={{ padding: '5px 12px', border: 'none', fontSize: 12, fontWeight: 600, cursor: 'pointer',
            background: value === b ? 'var(--text-primary)' : 'var(--bg-card)', color: value === b ? 'var(--bg-card)' : 'var(--text-secondary)' }}>
          {BLOCK_LABEL[b]}
        </button>
      ))}
    </div>
  );
}

function MetricCell({ player, block, metricKey }) {
  const mm = player[block].metrics[metricKey];
  const slots = blockSlots(player, block, metricKey);
  const last = [...slots].reverse().find(s => s.value != null);
  const title = cellTitle(block, metricKey, mm, last ? last.value : null);
  const spark = <Sparkline slots={slots} fitPoints={mm.state === 'not_applicable' ? null : mm.points} state={mm.state} block={block} />;
  if (mm.state === 'not_applicable') {
    return (
      <td style={td} title={NOT_APPLICABLE_LABEL}>
        <div style={{ fontSize: 11.5, color: 'var(--text-muted)', height: 20, display: 'flex', alignItems: 'center' }}>n/a</div>
        <div style={{ opacity: 0.6 }}>{spark}</div>
      </td>
    );
  }
  if (mm.state === 'insufficient') {
    return (
      <td style={td} title={title}>
        <div style={{ fontSize: 11.5, color: 'var(--text-muted)', height: 20, display: 'flex', alignItems: 'center' }}>— n = {mm.n}</div>
      </td>
    );
  }
  return (
    <td style={td} title={title}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, height: 20 }}>
        <StateChip state={mm.state} />
        <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>{fmtPct(mm.changePct)}</span>
      </div>
      <div style={{ marginTop: 3 }}>{spark}</div>
    </td>
  );
}

function PlayerName({ p }) {
  return (
    <div>
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap' }}>
        {p.name}
        {p.roleSource === 'override' && (
          <span title="Role set manually" style={{ marginLeft: 6, fontSize: 9.5, fontWeight: 700, padding: '1px 5px', borderRadius: 4, border: '1px solid var(--border-strong)', color: 'var(--text-secondary)', verticalAlign: 'middle' }}>
            MANUAL
          </span>
        )}
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{p.position} · {p.startsLast8}/8 started</div>
    </div>
  );
}

function Conclusions({ list }) {
  return (
    <ul style={{ margin: 0, paddingLeft: 14, fontSize: 12, lineHeight: 1.45, color: 'var(--text-secondary)' }}>
      {list.map((c, i) => <li key={i}>{c.text}</li>)}
    </ul>
  );
}

function GroupTable({ players, block, onOpen }) {
  let lastPos = null;
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={th}>Player</th>
            {METRICS.map(m => <th key={m.key} style={th}>{m.label}</th>)}
            <th style={{ ...th, width: '34%' }}>Conclusions</th>
          </tr>
        </thead>
        <tbody>
          {players.map(p => {
            const divider = p.position !== lastPos;
            lastPos = p.position;
            const usable = p[block].state === 'ok';
            return (
              <React.Fragment key={p.playerId}>
                {divider && (
                  <tr><td colSpan={METRICS.length + 2} style={{ padding: '10px 8px 3px', fontSize: 10.5, fontWeight: 700, color: 'var(--text-dimmed)', letterSpacing: 0.4 }}>{p.position}</td></tr>
                )}
                <tr className="tr-row" tabIndex={0} onClick={() => onOpen(p.playerId)}
                  onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(p.playerId); } }}
                  aria-label={`${p.name} — open details`}>
                  <td style={td}><PlayerName p={p} /></td>
                  {usable
                    ? METRICS.map(m => <MetricCell key={m.key} player={p} block={block} metricKey={m.key} />)
                    : <td colSpan={METRICS.length} style={{ ...td, fontSize: 12, color: 'var(--text-muted)' }}>{blockStatusText(p, block)}</td>}
                  <td style={td}><Conclusions list={p.conclusions} /></td>
                </tr>
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function PlayerCard({ p, block, onOpen }) {
  const usable = p[block].state === 'ok';
  return (
    <div className="tr-row" tabIndex={0} role="button" aria-label={`${p.name} — open details`}
      onClick={() => onOpen(p.playerId)} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(p.playerId); } }}
      style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, padding: 12 }}>
      <PlayerName p={p} />
      {usable ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(104px, 1fr))', gap: '8px 10px', marginTop: 10 }}>
          {METRICS.map(m => {
            const mm = p[block].metrics[m.key];
            return (
              <div key={m.key} title={mm.state === 'not_applicable' ? NOT_APPLICABLE_LABEL : stateVisual(mm.state).word}
                style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--text-primary)' }}>
                <span style={{ color: 'var(--text-muted)', minWidth: 34, fontSize: 11 }}>{m.label}</span>
                {mm.state === 'not_applicable' ? <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>n/a</span>
                  : mm.state === 'insufficient' ? <span style={{ color: 'var(--text-muted)' }}>—</span>
                  : <><StateChip state={mm.state} /><span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{fmtPct(mm.changePct)}</span></>}
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>{blockStatusText(p, block)}</div>
      )}
      <div style={{ marginTop: 10 }}><Conclusions list={p.conclusions} /></div>
    </div>
  );
}

function Group({ title, players, defaultBlock, narrow, onOpen }) {
  const [block, setBlock] = useState(defaultBlock);
  return (
    <section style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: narrow ? 12 : 14, marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>
          {title} <span style={{ fontWeight: 500, color: 'var(--text-muted)', fontSize: 13 }}>({players.length})</span>
        </h3>
        <BlockSwitch value={block} onChange={setBlock} label={title} />
      </div>
      {players.length === 0 ? (
        <div style={{ fontSize: 12.5, color: 'var(--text-muted)', padding: '10px 0' }}>No players in this group with the current filter.</div>
      ) : narrow ? (
        <div style={{ display: 'grid', gap: 10 }}>{players.map(p => <PlayerCard key={p.playerId} p={p} block={block} onOpen={onOpen} />)}</div>
      ) : (
        <GroupTable players={players} block={block} onOpen={onOpen} />
      )}
    </section>
  );
}

function Skeleton() {
  return (
    <div aria-busy="true" aria-label="Loading trends">
      {[0, 1].map(g => (
        <div key={g} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: 14, marginBottom: 16 }}>
          <div className="tr-skel" style={{ width: 140, height: 16, marginBottom: 14 }} />
          {Array.from({ length: 5 }, (_, i) => (
            <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
              <div className="tr-skel" style={{ width: 150, height: 30 }} />
              {METRICS.map(m => <div key={m.key} className="tr-skel" style={{ flex: 1, height: 30 }} />)}
              <div className="tr-skel" style={{ flex: 3, height: 30 }} />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function Message({ title, body }) {
  return (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: '40px 20px', textAlign: 'center' }}>
      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>{title}</div>
      {body && <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 6 }}>{body}</div>}
    </div>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────────────────────────
export default function Trends({ user }) {
  const narrow = useIsNarrow();
  const [asOf, setAsOf] = useUrlParam('as_of', '');
  const effectiveAsOf = asOf || todayStr();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [trendOnly, setTrendOnly] = useState(false);
  const [openId, setOpenId] = useState(null);
  const token = user?.sessionToken;

  const load = useCallback(() => {
    if (!token) { setLoading(false); setError('session'); return Promise.resolve(); }
    setLoading(true); setError(null);
    return getGpsTrends(token, effectiveAsOf)
      .then(d => setData(d))
      .catch(e => setError(String(e.message || e).includes('401') ? 'session' : 'load'))
      .finally(() => setLoading(false));
  }, [token, effectiveAsOf]);

  useEffect(() => { load(); }, [load]);

  const closeDrawer = useCallback(() => setOpenId(null), []);
  const onRoleChange = useCallback(async (playerId, role) => {
    if (role === 'auto') await clearTrendRoleOverride(token, playerId);
    else await setTrendRoleOverride(token, playerId, role);
    await load();
  }, [token, load]);

  const players = useMemo(() => data?.players || [], [data]);
  const summary = useMemo(() => {
    const counts = {};
    players.forEach(p => { const k = summaryKey(p.headline); counts[k] = (counts[k] || 0) + 1; });
    return SUMMARY_ORDER.filter(s => counts[s.key]).map(s => `${counts[s.key]} ${s.label}`).join(' · ');
  }, [players]);
  const visible = trendOnly ? players.filter(hasTrend) : players;
  const basis = sortPlayers(visible.filter(p => p.role === 'basis'));
  const wissels = sortPlayers(visible.filter(p => p.role !== 'basis'));
  const openPlayer = openId != null ? players.find(p => p.playerId === openId) : null;

  return (
    <div className="trends-root" style={{ maxWidth: 1400, margin: '0 auto', paddingBottom: 40 }}>
      <style>{TRENDS_CSS}</style>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 12 }}>
        <div>
          <h2 style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>Trends — last 8 matches / weeks</h2>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
            Match: per 90 min, 60+ min only · Training: weekly total vs squad median · Colour = direction, not good/bad
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-secondary)' }}>
            As of
            <input type="date" value={effectiveAsOf} max={todayStr()} onChange={e => setAsOf(e.target.value === todayStr() ? '' : e.target.value)}
              style={{ padding: '5px 8px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: 12, colorScheme: 'light dark' }} />
          </label>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-secondary)', cursor: 'pointer' }}>
            <input type="checkbox" checked={trendOnly} onChange={e => setTrendOnly(e.target.checked)} />
            Only players with a trend
          </label>
        </div>
      </div>

      {data && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, padding: '9px 12px', marginBottom: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{summary || 'No players'}</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Provisional bands — calibrated {fmtDate(data.meta.calibratedAt)}</div>
        </div>
      )}

      {error === 'session' ? (
        <Message title="Session expired" body="Log in again to view Trends." />
      ) : error ? (
        <Message title="Trends could not be loaded" body={<button onClick={load} style={{ marginTop: 8, padding: '6px 12px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer' }}>Try again</button>} />
      ) : loading && !data ? (
        <Skeleton />
      ) : players.length === 0 ? (
        <Message title="No players" body="There are no active outfield players with GPS data this season." />
      ) : (
        <div style={{ opacity: loading ? 0.6 : 1, transition: 'opacity 0.15s' }}>
          <Group title="Starters" players={basis} defaultBlock="match" narrow={narrow} onOpen={setOpenId} />
          <Group title="Substitutes" players={wissels} defaultBlock="training" narrow={narrow} onOpen={setOpenId} />
        </div>
      )}

      {openPlayer && (
        <TrendsPlayerDrawer player={openPlayer} isAdmin={!!user?.isAdmin} fullScreen={narrow}
          onClose={closeDrawer} onRoleChange={onRoleChange} />
      )}
    </div>
  );
}
